import type { ArrowType, CreatureType, Faction, UpgradeId } from "./types";

// ── Arena geometry ────────────────────────────────────────────────────────────
export const ARENA = {
  WIDTH: 120,
  DEPTH: 120,
  /** radius of the central no-man's land */
  NEUTRAL_RADIUS: 10,
  /** y height of the ground plane */
  GROUND_Y: 0,
} as const;

export const FACTION_SPAWN: Record<Faction, { x: number; z: number }> = {
  dawn: { x: -48, z: 0 },
  dusk: { x: 48, z: 0 },
};

export const FACTION_COLOR: Record<Faction, number> = {
  dawn: 0xf59e0b,
  dusk: 0x8b5cf6,
};

// ── Match rules ───────────────────────────────────────────────────────────────
export const MATCH_DURATION_MS = 8 * 60 * 1000;
export const POLL_INTERVAL_MS = 100;
export const ACTION_COOLDOWN_MS = 250;
export const RESPAWN_MS = 3000;
export const MAX_EVENTS = 60;
export const MAX_ARROWS_IN_FLIGHT = 80;

// ── Players ───────────────────────────────────────────────────────────────────
export const PLAYER = {
  MAX_HP: 100,
  BASE_DAMAGE: 12,
  BASE_LEVEL: 1,
  XP_PER_LEVEL: 50,
  XP_GROWTH: 1.35,
  REGEN_BASE: 0,
  REGEN_PER_UPGRADE: 2,
  MOVE_SPEED: 14,
} as const;

export function xpForLevel(level: number): number {
  return Math.floor(PLAYER.XP_PER_LEVEL * Math.pow(PLAYER.XP_GROWTH, level - 1));
}

// ── Arrows ────────────────────────────────────────────────────────────────────
export interface ArrowSpec {
  name: string;
  color: number;
  damageMult: number;
  /** seconds of flight for FX */
  flightScale: number;
  /** special effect description for HUD */
  blurb: string;
  /** unlock level */
  unlockLevel: number;
}

export const ARROW_TYPES: Record<ArrowType, ArrowSpec> = {
  standard: {
    name: "Standard",
    color: 0xd1d5db,
    damageMult: 1.0,
    flightScale: 1.0,
    blurb: "A humble wooden arrow.",
    unlockLevel: 1,
  },
  fire: {
    name: "Fire",
    color: 0xf97316,
    damageMult: 1.25,
    flightScale: 0.95,
    blurb: "Burning tips that scorch the battlefield.",
    unlockLevel: 2,
  },
  frost: {
    name: "Frost",
    color: 0x38bdf8,
    damageMult: 1.1,
    flightScale: 1.0,
    blurb: "Icy shards that chill foes.",
    unlockLevel: 3,
  },
  venom: {
    name: "Venom",
    color: 0x4ade80,
    damageMult: 1.4,
    flightScale: 1.05,
    blurb: "Toxic tips deal heavy damage.",
    unlockLevel: 4,
  },
  lightning: {
    name: "Lightning",
    color: 0xfacc15,
    damageMult: 1.6,
    flightScale: 0.7,
    blurb: "Blazing fast bolts of pure storm.",
    unlockLevel: 5,
  },
  starfall: {
    name: "Starfall",
    color: 0xa78bfa,
    damageMult: 2.0,
    flightScale: 1.2,
    blurb: "Shards of a fallen star. Devastating.",
    unlockLevel: 7,
  },
  chaos: {
    name: "Chaos",
    color: 0xf472b6,
    damageMult: 2.5,
    flightScale: 0.8,
    blurb: "Reality-bending arrows of pure mayhem.",
    unlockLevel: 9,
  },
};

export const ARROW_ORDER: ArrowType[] = [
  "standard",
  "fire",
  "frost",
  "venom",
  "lightning",
  "starfall",
  "chaos",
];

// ── Upgrades ──────────────────────────────────────────────────────────────────
export interface UpgradeSpec {
  name: string;
  description: string;
  maxLevel: number;
  /** cost in "fame" (earned per kill) */
  baseCost: number;
  costGrowth: number;
  icon: string;
}

export const UPGRADES: Record<UpgradeId, UpgradeSpec> = {
  bow_power: {
    name: "Bow Power",
    description: "+20% arrow damage per level",
    maxLevel: 10,
    baseCost: 5,
    costGrowth: 1.6,
    icon: "🏹",
  },
  arrow_speed: {
    name: "Arrow Speed",
    description: "Arrows fly 12% faster per level",
    maxLevel: 5,
    baseCost: 4,
    costGrowth: 1.8,
    icon: "💨",
  },
  max_hp: {
    name: "Vitality",
    description: "+25 max HP per level",
    maxLevel: 8,
    baseCost: 4,
    costGrowth: 1.5,
    icon: "❤️",
  },
  regen: {
    name: "Regeneration",
    description: "+2 HP/s per level",
    maxLevel: 5,
    baseCost: 6,
    costGrowth: 1.9,
    icon: "✨",
  },
  multishot: {
    name: "Multishot",
    description: "+1 arrow per shot per level",
    maxLevel: 3,
    baseCost: 12,
    costGrowth: 2.2,
    icon: "🎯",
  },
  pierce: {
    name: "Pierce",
    description: "Arrows hit +1 extra target per level",
    maxLevel: 3,
    baseCost: 10,
    costGrowth: 2.0,
    icon: "🗡️",
  },
  crit_chance: {
    name: "Critical Aim",
    description: "+8% crit chance per level (crits deal 2x)",
    maxLevel: 5,
    baseCost: 8,
    costGrowth: 1.8,
    icon: "💥",
  },
  arrow_type: {
    name: "Arrow Craft",
    description: "Unlock new magical arrow types",
    maxLevel: 7,
    baseCost: 8,
    costGrowth: 2.0,
    icon: "🔮",
  },
};

export const UPGRADE_ORDER: UpgradeId[] = [
  "bow_power",
  "arrow_speed",
  "max_hp",
  "regen",
  "crit_chance",
  "multishot",
  "pierce",
  "arrow_type",
];

export function upgradeCost(spec: UpgradeSpec, currentLevel: number): number {
  return Math.floor(spec.baseCost * Math.pow(spec.costGrowth, currentLevel));
}

export function playerDamage(player: {
  upgrades: Record<UpgradeId, number>;
  arrowType: ArrowType;
  level: number;
}): number {
  const power = 1 + 0.2 * (player.upgrades.bow_power ?? 0);
  const levelBonus = 1 + 0.05 * (player.level - 1);
  return PLAYER.BASE_DAMAGE * power * levelBonus * ARROW_TYPES[player.arrowType].damageMult;
}

export function playerMaxHp(upgrades: Record<UpgradeId, number>): number {
  return PLAYER.MAX_HP + 25 * (upgrades.max_hp ?? 0);
}

export function playerRegen(upgrades: Record<UpgradeId, number>): number {
  return PLAYER.REGEN_BASE + PLAYER.REGEN_PER_UPGRADE * (upgrades.regen ?? 0);
}

export function playerCritChance(upgrades: Record<UpgradeId, number>): number {
  return Math.min(0.5, 0.05 * (upgrades.crit_chance ?? 0));
}

export function arrowFlightMs(upgrades: Record<UpgradeId, number>, arrowType: ArrowType): number {
  const base = 600 * ARROW_TYPES[arrowType].flightScale;
  const speedMult = 1 + 0.12 * (upgrades.arrow_speed ?? 0);
  return base / speedMult;
}

// ── Creatures ─────────────────────────────────────────────────────────────────
export interface CreatureSpec {
  name: string;
  hp: number;
  speed: number;
  damage: number;
  xp: number;
  scale: number;
  color: number;
  attackRange: number;
  attackCooldownMs: number;
}

export const CREATURE_TYPES: Record<CreatureType, CreatureSpec> = {
  goblin: {
    name: "Goblin",
    hp: 30,
    speed: 10,
    damage: 6,
    xp: 8,
    scale: 0.8,
    color: 0x65a30d,
    attackRange: 3,
    attackCooldownMs: 1200,
  },
  ghoul: {
    name: "Ghoul",
    hp: 70,
    speed: 6,
    damage: 12,
    xp: 15,
    scale: 1.2,
    color: 0x94a3b8,
    attackRange: 2.5,
    attackCooldownMs: 1800,
  },
  bug: {
    name: "War Bug",
    hp: 18,
    speed: 16,
    damage: 4,
    xp: 5,
    scale: 0.6,
    color: 0x7c2d12,
    attackRange: 2,
    attackCooldownMs: 800,
  },
};

export const CREATURE_ORDER: CreatureType[] = ["goblin", "ghoul", "bug"];

/** how creature stats scale with arena tick count (waves) */
export function creatureWaveMultiplier(tickCount: number): number {
  return 1 + Math.floor(tickCount / 30) * 0.15;
}
